import type { Action, Middleware } from 'redux';
import type { CardEditorType, StoryModel } from 'types/story';
import { ADMIN_SESSION, STORY_VERSIONS } from 'common/constants';
import type { StoryEditBatchRequest, StoryEditBatchResponse } from 'common/story-edit-operations/types';
import UrlHelper from 'utils/url-helper';
import translate from 'utils/translate';
import { getResponseErrorMsg } from 'common/resources/api-response';
import { storyEditOperations } from 'admin/resources';
import { createAiServerHeaders } from 'admin/queries/story-chat/ai-server-fetch';
import { toast } from 'admin/components/common/MessageContainer/toast';
import type { AdminReducerState, AdminThunkDispatch } from 'admin/reducers';
import { handleStoryNoAccessError } from 'admin/actions/helpers';
import { selectStoryFacade } from 'admin/reducers/story-editor/selectors';
import { AUTO_SYNC_TARGET } from 'admin/constants/common';
import {
	CARD_EDITOR_SYNC,
	FLUSH_AUTO_SYNC,
	STORY_EDIT_OPERATIONS,
	STORY_EDITOR_SYNC,
	STORY_EDITOR_UNMOUNT,
} from 'admin/constants/actions';
import { getMergeOfEditableStoryWithCardEditor } from 'admin/actions/card-editor/card-editor-sync';
import { isStoryLoadNormalizedAction } from 'admin/actions/story/get-story';
import { catchUpStory } from 'admin/actions/story/catch-up-story';
import {
	captureStorySave,
	type EditCaptureMark,
	type EditCaptureResult,
} from 'admin/utils/story-edit-operations/save-capture';
import { createHeldNormalization } from 'admin/utils/story-edit-operations/held-normalization';
import {
	createStoryEditSaver,
	storyEditSaverFor,
	type StoryEditNoAccessStatus,
} from 'admin/utils/story-edit-operations/saver';
import {
	deliverEditCapture,
	logExcludedEditCapture,
	logHeldNormalization,
	type EditCaptureSource,
} from 'admin/utils/story-edit-operations/transport';

type SaveBoundAction = Action & {
	meta: { autoSync: keyof typeof AUTO_SYNC_TARGET; editCapture?: EditCaptureMark };
};

/**
 * Same criterion as autosave: a local write that autosave persists carries `meta.autoSync`.
 * Tab synchronization strips it and chat writes disable it, so neither is captured here.
 */
function isSaveBound(action: unknown): action is SaveBoundAction {
	if (typeof action !== 'object' || action === null || !('meta' in action)) return false;
	const { meta } = action;
	if (typeof meta !== 'object' || meta === null || !('autoSync' in meta)) return false;
	return meta.autoSync === AUTO_SYNC_TARGET.CARD_EDITOR || meta.autoSync === AUTO_SYNC_TARGET.STORY_EDITOR;
}

type PersistedStory = (story: StoryModel, cardEditor: CardEditorType) => StoryModel;

/**
 * Card editor autosave persists the story merged with the edited card; the merge also synchronizes
 * global block references in every card. The last merge is reused: while the global blocks stay the
 * same, every other card is already synchronized in it, so merging onto it changes only the edited
 * card and the other cards keep their references (and are skipped by the comparison).
 */
function createPersistedStory(): PersistedStory {
	let cache: { story: StoryModel; cardEditor: CardEditorType; merged: StoryModel } | undefined;
	return (story, cardEditor) => {
		if (cache?.story === story && cache.cardEditor === cardEditor) return cache.merged;
		const isSameGlobals =
			cache?.story === story &&
			cache.cardEditor.storyElements === cardEditor.storyElements &&
			cache.cardEditor.data?._id === cardEditor.data?._id;
		const base = isSameGlobals && cache ? cache.merged : story;
		const merged = getMergeOfEditableStoryWithCardEditor(base, cardEditor, STORY_VERSIONS.latest);
		cache = { story, cardEditor, merged };
		return merged;
	};
}

/** Posts one batch to the AI server with the admin session. */
async function postStoryEditBatch(storyId: string, request: StoryEditBatchRequest): Promise<StoryEditBatchResponse> {
	if (!UrlHelper.chat) throw new Error('The edit operations server URL is not configured');
	const token = localStorage.getItem(ADMIN_SESSION);
	if (!token) throw new Error('A session is required to deliver edit operations');
	const response = await storyEditOperations.post.params({ storyId }).send<{ body: StoryEditBatchResponse }>({
		data: request,
		headers: { ...createAiServerHeaders(), 'x-session-token': token },
	});
	return response.body;
}

/** Same handling and toast as a failed snapshot save (`createThunkAction` with `onUpdateVersionError`). */
function handleNoAccess(dispatch: AdminThunkDispatch, status: StoryEditNoAccessStatus, error: unknown) {
	const responseError = error as IBackendResponseError;
	let message = getResponseErrorMsg(responseError, translate('responseError.badRequest'));
	try {
		// Logs out on 403 and throws its message.
		handleStoryNoAccessError(dispatch, responseError);
		if (status === 404) message = translate('responseError.404');
	} catch (noAccessError) {
		message = (noAccessError as Error).message;
	}
	toast.error(message, 5);
}

/** The next response that needs it catches up again; a failed load has already shown its toast. */
const logCatchUpFailure = (error: unknown) => console.warn('[edit-operations] catch-up failed', error);

type Delivery = { storyId: string; source: EditCaptureSource; action: string };

const typeOf = (action: unknown) =>
	typeof action === 'object' && action !== null && 'type' in action ? action.type : undefined;

/**
 * Card editor writes: compare the story as card editor autosave persists it, so other cards are included.
 * The write that fills the empty editor compares the story with the card as the editor loaded it.
 */
function captureCardEditorWrite(
	persisted: PersistedStory,
	story: StoryModel,
	before: CardEditorType,
	after: CardEditorType
): EditCaptureResult {
	if (before.data && before.data._id !== after.data?._id) {
		return { status: 'unsupported', reason: 'The edited card changed within one write' };
	}
	return captureStorySave(persisted(story, before), persisted(story, after));
}

/**
 * The single capture point: sends edit operations for every save-bound write, by comparing
 * the story autosave persists before and after the reducer update. Loading a story or a card
 * is not an edit: what it normalizes is held and sent with the first edit (see held normalization).
 */
export const editOperationsMiddleware = (): Middleware<{}, AdminReducerState, AdminThunkDispatch> => store => {
	const persisted = createPersistedStory();
	const normalization = createHeldNormalization();

	const createSaver = (storyId: string) =>
		createStoryEditSaver({
			storyId,
			post: request => postStoryEditBatch(storyId, request),
			baseOpCounter: () => {
				const { story } = store.getState().storyEditor;
				return story?.id === storyId ? story.opCounter ?? 0 : 0;
			},
			onStatus: status => {
				store.dispatch({ type: STORY_EDITOR_SYNC[status] });
				store.dispatch({ type: CARD_EDITOR_SYNC[status] });
			},
			onSaved: response =>
				store.dispatch({
					type: STORY_EDIT_OPERATIONS.SAVED,
					payload: { storyId, opCounter: response.opCounter },
				}),
			// Also after the editor left the story: the payload, not the loaded story, names it to other tabs.
			onAcknowledged: () => store.dispatch({ type: STORY_EDIT_OPERATIONS.ACKNOWLEDGED, payload: { storyId } }),
			onSkipped: () => toast.warn('Some edits could not be applied because the story changed.'),
			onRejected: status => toast.error(`Edit operations: the server rejected the changes (${status}).`),
			onNoAccess: (status, error) => handleNoAccess(store.dispatch, status, error),
			onCatchUpNeeded: () => void store.dispatch(catchUpStory(storyId)).catch(logCatchUpFailure),
		});

	/** Delivers through the story's saver, created on the story's first delivery in this tab. */
	const deliver = (capture: EditCaptureResult, delivery: Delivery) => {
		if (!storyEditSaverFor(delivery.storyId)) createSaver(delivery.storyId);
		deliverEditCapture(capture, delivery);
	};

	/** Sends the edited story's queued actions now; resolves once its queue is empty or a send fails. */
	const flushEditedStory = async () => {
		const storyId = store.getState().storyEditor.story?.id;
		if (storyId) await storyEditSaverFor(storyId)?.flush();
	};

	/** Holds a load's normalization; a load without one, or one that cannot be captured, is only reported. */
	const hold = (capture: EditCaptureResult, delivery: Delivery, { replace }: { replace: boolean }) => {
		const changes = capture.status === 'captured' ? capture.changes : [];
		if (replace) normalization.replace(delivery.storyId, changes);
		else normalization.add(delivery.storyId, changes);
		if (capture.status === 'captured') {
			logHeldNormalization(delivery.source, { action: delivery.action, changeCount: changes.length });
		} else {
			deliver(capture, delivery);
		}
	};

	/** Sends an action, with the held normalization ahead of the first one that has changes. */
	const send = (capture: EditCaptureResult, delivery: Delivery) => {
		const sent = normalization.attach(delivery.storyId, capture);
		const action = sent === capture ? delivery.action : `${delivery.action} with load normalization`;
		deliver(sent, { ...delivery, action });
	};

	return next => action => {
		if (isStoryLoadNormalizedAction(action)) {
			const { persisted: before, normalized } = action.payload;
			const delivery = { storyId: normalized.id, source: 'story-editor', action: action.type } as const;
			hold(captureStorySave(before, normalized), delivery, { replace: true });
			return next(action);
		}
		if (typeOf(action) === FLUSH_AUTO_SYNC) return flushEditedStory().then(() => next(action));
		if (typeOf(action) === STORY_EDITOR_UNMOUNT) {
			// Leaving the story sends its queued actions with its opCounter before the editor state is reset;
			// the saver keeps delivering afterwards. Leaving without an edit leaves the persisted story as it was.
			void flushEditedStory();
			normalization.drop();
		}
		if (!isSaveBound(action)) return next(action);

		const before = store.getState();
		const source: EditCaptureSource =
			action.meta.autoSync === AUTO_SYNC_TARGET.CARD_EDITOR ? 'card-editor' : 'story-editor';
		// The developer switch that turns card editor autosave off also keeps its writes off the server.
		const isCardEditorSyncOff =
			source === 'card-editor' &&
			before.storyEditor.story !== null &&
			!selectStoryFacade(before).isCardEditorAutoSyncEnabled;
		if (action.meta.editCapture === 'excluded' || isCardEditorSyncOff) {
			logExcludedEditCapture(source, { action: action.type });
			return next(action);
		}

		const result = next(action);
		const after = store.getState();

		const { story } = after.storyEditor;
		if (!story?.id) return result;
		const delivery = { storyId: story.id, source, action: action.type };

		if (source === 'card-editor') {
			if (before.cardEditor.present !== after.cardEditor.present) {
				const capture = captureCardEditorWrite(
					persisted,
					story,
					before.cardEditor.present,
					after.cardEditor.present
				);
				const isCardLoad = !before.cardEditor.present.data;
				if (isCardLoad) hold(capture, delivery, { replace: false });
				else send(capture, delivery);
			}
		} else if (before.storyEditor.story !== story) {
			send(captureStorySave(before.storyEditor.story, story), delivery);
		}
		return result;
	};
};
